import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAssessment } from "@/context/AssessmentContext";
import { RUOLI, STILI_DANZA } from "@/data/questions";
import { trackAssessmentStarted } from "@/lib/analytics";
import BDFLogo from "@/components/BDFLogo";

export default function UserInfo() {
  const navigate = useNavigate();
  const { userInfo, setUserInfo } = useAssessment();
  const [form, setForm] = useState({
    nome: userInfo?.nome || "",
    cognome: userInfo?.cognome || "",
    email: userInfo?.email || "",
    ruolo: userInfo?.ruolo || "",
    stile_danza: userInfo?.stile_danza || "",
  });

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const validate = () => {
    if (!form.nome.trim()) return "Inserisci il tuo nome";
    if (!form.cognome.trim()) return "Inserisci il tuo cognome";
    if (!form.email.trim()) return "Inserisci la tua email";
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) return "Inserisci un'email valida";
    if (!form.ruolo) return "Seleziona il tuo ruolo";
    if (!form.stile_danza) return "Seleziona lo stile di danza";
    return null;
  };

  const handleNext = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setUserInfo(form);
    trackAssessmentStarted({ ruolo: form.ruolo, stile_danza: form.stile_danza });
    navigate("/assessment");
  };

  return (
    <div className="min-h-screen w-full">
      {/* Header */}
      <header className="px-6 sm:px-10 lg:px-16 pt-8">
        <div className="flex items-center justify-between max-w-[1100px] mx-auto">
          <button onClick={() => navigate("/")} data-testid="back-home-button" className="cursor-pointer">
            <BDFLogo />
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="userinfo-back-link"
            className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#5C7368] hover:text-[#1E3F33] transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Indietro
          </button>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[#5C7368] mb-4" data-testid="userinfo-step-label">
            Step 1 — Le tue informazioni
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#1E3F33] tracking-tight" data-testid="userinfo-title">
            Parliamo
            <br />
            <em className="italic font-normal">di te.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C7368] leading-relaxed" data-testid="userinfo-subtitle">
            Prima di iniziare, raccogliamo qualche informazione per personalizzare il tuo profilo respiratorio.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-12 grid sm:grid-cols-2 gap-6 max-w-3xl"
        >
          <Field label="Nome" testid="field-nome">
            <Input
              data-testid="input-nome"
              value={form.nome}
              onChange={(e) => update("nome", e.target.value)}
              placeholder="Es. Maria"
              className="h-12 rounded-xl bg-white border-[#DCE3DF] focus-visible:ring-2 focus-visible:ring-[#C5D1C9] focus-visible:ring-offset-0"
            />
          </Field>
          <Field label="Cognome" testid="field-cognome">
            <Input
              data-testid="input-cognome"
              value={form.cognome}
              onChange={(e) => update("cognome", e.target.value)}
              placeholder="Es. Rossi"
              className="h-12 rounded-xl bg-white border-[#DCE3DF] focus-visible:ring-2 focus-visible:ring-[#C5D1C9] focus-visible:ring-offset-0"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Email" testid="field-email">
              <Input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="mariarossi@email.com"
                className="h-12 rounded-xl bg-white border-[#DCE3DF] focus-visible:ring-2 focus-visible:ring-[#C5D1C9] focus-visible:ring-offset-0"
              />
            </Field>
          </div>
          <Field label="Ruolo" testid="field-ruolo">
            <Select value={form.ruolo} onValueChange={(v) => update("ruolo", v)}>
              <SelectTrigger
                data-testid="select-ruolo"
                className="h-12 rounded-xl bg-white border-[#DCE3DF] focus:ring-2 focus:ring-[#C5D1C9] focus:ring-offset-0"
              >
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent>
                {RUOLI.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`option-ruolo-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Stile di danza" testid="field-stile">
            <Select value={form.stile_danza} onValueChange={(v) => update("stile_danza", v)}>
              <SelectTrigger
                data-testid="select-stile"
                className="h-12 rounded-xl bg-white border-[#DCE3DF] focus:ring-2 focus:ring-[#C5D1C9] focus:ring-offset-0"
              >
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {STILI_DANZA.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`option-stile-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 max-w-3xl"
        >
          <Button
            onClick={handleNext}
            data-testid="userinfo-next-button"
            size="lg"
            className="rounded-full px-8 h-14 bg-[#1E3F33] hover:bg-[#2A5243] text-white text-sm tracking-[0.08em] uppercase font-semibold transition-all hover:shadow-xl group"
          >
            Inizia il questionario
            <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
          <span className="text-xs text-[#5C7368]" data-testid="userinfo-privacy">
            I tuoi dati sono utilizzati solo per personalizzare l'esperienza.
          </span>
        </motion.div>
      </main>
    </div>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-xs uppercase tracking-[0.16em] text-[#5C7368] mb-2 block font-medium">
      {label}
    </Label>
    {children}
  </div>
);
